import { useReceivedInvoiceBulkManualUpload } from '@/context/services/invoice/ReceivedInvoice.service';
import {
  RECEIVED_INVOICE_FILE_ERROR,
  TReceivedInvoiceUpload,
} from '@/features/invoices/components/List/Table/Uploader/type';
import { RECEIVED_INVOICE_DEFAULT } from '@/features/invoices/components/List/Table/Uploader/Uploader';
import { useErrorTrackingSystem } from '@/hooks/useErrorTrackingSystem';
import { useTranslation } from '@/i18n';
import { ToastAPI } from '@moneyforward/ap-frontend-components';
import { RcFile } from 'antd/es/upload';
import {
  BulkUploadReceivedInvoicesRequestFilesItem,
  BulkUploadReceivedInvoicesResult,
} from 'ap-openapi';
import { useCallback } from 'react';
import {
  SubmitHandler,
  UseFieldArrayReplace,
  UseFormReset,
  UseFormSetError,
} from 'react-hook-form';

type TSubmitBulkManualUploadProps = {
  setHeaderErrorMsg: (msg: string) => void;
  setFileErrorMsg: (
    errorMsg: string,
    index: number,
    error: RECEIVED_INVOICE_FILE_ERROR
  ) => void;
  setError: UseFormSetError<TReceivedInvoiceUpload>;
  setIsLoading: (isLoading: boolean) => void;
  notify: ToastAPI;
  replace: UseFieldArrayReplace<TReceivedInvoiceUpload, 'files'>;
  reset: UseFormReset<TReceivedInvoiceUpload>;
  callRefetch: VoidFunction;
};

export const useSubmitBulkManualUpload = ({
  setHeaderErrorMsg,
  setFileErrorMsg,
  setError,
  setIsLoading,
  notify,
  replace,
  reset,
  callRefetch,
}: TSubmitBulkManualUploadProps) => {
  const { send } = useErrorTrackingSystem();
  const { mutateAsync } = useReceivedInvoiceBulkManualUpload();
  const { t } = useTranslation();

  const onSubmit: SubmitHandler<TReceivedInvoiceUpload> = useCallback(
    async (form) => {
      try {
        setHeaderErrorMsg('');
        setIsLoading(true);
        const resp = await mutateAsync({
          ap_payee_id: form.ap_payee_id,
          // eslint-disable-next-line @typescript-eslint/consistent-type-assertions
          files: createToManualFile(
            form.files
          ) as BulkUploadReceivedInvoicesRequestFilesItem[],
          pic_ids: form.pic_ids,
          receipt_type: form.receipt_type,
        });
        if (resp.status === 'success') {
          resp.results?.map(
            (result: BulkUploadReceivedInvoicesResult, index: number) => {
              if (result.status === 'error') {
                // eslint-disable-next-line @typescript-eslint/no-unused-vars
                let fileErrorMsg;
                Object.entries(result.errors ?? []).forEach(([key, value]) => {
                  if (key === 'file') {
                    fileErrorMsg = value.map((error) => error.message).join('');
                  } else {
                    // eslint-disable-next-line @typescript-eslint/consistent-type-assertions
                    setError(key as keyof TReceivedInvoiceUpload, {
                      type: 'validate',
                      message: value.map((error) => error.message).join(''),
                    });
                  }
                });
                if (fileErrorMsg) {
                  setFileErrorMsg(
                    fileErrorMsg,
                    index,
                    RECEIVED_INVOICE_FILE_ERROR.INVALID
                  );
                }
              }
            }
          );
          const errorFiles = form.files.filter((file) => file.errorMessage);
          if (errorFiles.length > 0) {
            setHeaderErrorMsg(
              t('received_invoice.uploader.file_upload.error_message')
            );
            replace(errorFiles);
          } else {
            callRefetch();
            notify.success({
              title: t('received_invoice.uploader.file_upload.success_message'),
              duration: 5,
            });
            reset(RECEIVED_INVOICE_DEFAULT);
          }
        } else if (resp.status === 'invalid') {
          const errors = Object.values(resp.errors)
            .flatMap((entries) =>
              // eslint-disable-next-line @typescript-eslint/consistent-type-assertions
              Object.values(entries as Record<string, Error[]>).flat()
            )
            .map((error) => error.message)
            .join('');
          setHeaderErrorMsg(errors);
        } else {
          setHeaderErrorMsg(
            resp.errors.messages?.join('') ??
              t('received_invoice.uploader.error_message')
          );
        }
      } catch (error) {
        if (error instanceof Error && error?.name === 'AxiosError') {
          send(error.message);
        } else {
          // eslint-disable-next-line @typescript-eslint/consistent-type-assertions
          send(error as Error);
        }
        setHeaderErrorMsg(t('received_invoice.uploader.error_message'));
      } finally {
        setIsLoading(false);
      }
    },
    [
      callRefetch,
      mutateAsync,
      notify,
      replace,
      reset,
      send,
      setError,
      setFileErrorMsg,
      setHeaderErrorMsg,
      setIsLoading,
      t,
    ]
  );
  return {
    onSubmit,
  };
};

export const createToManualFile = (files: TReceivedInvoiceUpload['files']) => {
  return files.map((file) => {
    return {
      ...file.fileOption,
      // eslint-disable-next-line @typescript-eslint/consistent-type-assertions
      file: file as RcFile,
    };
  });
};
